/**
 * Proven-by-evals section (inside `CapabilityDrawer`) — the agent evals linked
 * to this capability, each with its latest default-alias full-run verdict and
 * pass rate. Read-only; rendered only when the capability has linked evals.
 *
 * @module features/product-map/components/LinkedEvalsSection
 */

import { Link } from '@tanstack/react-router';
import { ArrowSquareOut } from '@phosphor-icons/react';
import { Badge, ListCard, Text } from '@chatsdet/ui';
import { evalVerdictLabel, evalVerdictVariant, formatPassRate } from '../evalProof';
import type { LinkedEval } from '../types';

type Props = {
  evals: LinkedEval[];
};

export function LinkedEvalsSection({ evals }: Props) {
  return (
    <ListCard title="Proven by evals" titleAccessory={`${evals.length} linked`}>
      {evals.map((evalItem) => {
        const run = evalItem.latestRun;
        return (
          <div key={evalItem.evalId} className="group flex items-center gap-2 px-4 py-2.5 hover:bg-muted/50">
            <span className="min-w-0 flex-1 text-pretty line-clamp-2 text-sm text-foreground">
              {evalItem.name}
            </span>
            {run ? (
              <>
                <Badge size="sm" variant={evalVerdictVariant(run.verdict)}>
                  {evalVerdictLabel(run.verdict)}
                </Badge>
                <Text size="xs" className="shrink-0 tabular-nums text-muted-foreground">
                  {formatPassRate(run.passRate)}
                </Text>
                <Text size="xs" className="shrink-0 text-muted-foreground">
                  {new Date(run.finishedAt).toLocaleDateString()}
                </Text>
                <Link
                  to="/agent-evals/runs/$runId"
                  params={{ runId: run.runId }}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`View latest run of ${evalItem.name}`}
                  className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
                >
                  <ArrowSquareOut size={14} />
                </Link>
              </>
            ) : (
              <Text size="xs" className="shrink-0 text-muted-foreground">
                No completed full run yet
              </Text>
            )}
          </div>
        );
      })}
    </ListCard>
  );
}
